<template>
    <div>
        <ReportTemplate
            :loading="!initialized"
            title="Inventory On Hand Report"
            :permission="PERMISSION_CODES.CMS_VIEW_REPORT_INVENTORY_ON_HAND"
            :filters="filters"
            :columns="columns"
            fetchRoute="/reports/inventory-on-hand"
            :reportTypeCode="REPORT_MASTER_CODES.INVENTORY_ON_HAND"
            :showDateFilter="false"
        >
            <div class="inventory-report-note">
                Choose a report view to consolidate stock by product, retain package identity, or compare products by location.
                Room details include configured custom rooms as well as Front Room and Back Room.
            </div>
        </ReportTemplate>
    </div>
</template>

<script>
import ReportTemplate from "../ReportTemplate.vue"
import { mapState } from "vuex"
import ReportFilter from "../../../models/ReportFilter"
import { PERMISSION_CODES } from "../../../store/permission"

export default {
    components: { ReportTemplate },
    data() {
        return {
            filters: [],
            initialized: false,
            intervalId: null,
            PERMISSION_CODES,
            columns: [
                {
                    title: "Package",
                    key: "package_no",
                },
                {
                    title: "Location",
                    key: "warehouseName",
                },
                {
                    title: "Room Breakdown",
                    key: "room",
                },
                {
                    title: "Product",
                    key: "name",
                },
                {
                    title: "Display Name",
                    key: "display_name",
                },
                {
                    title: "Classification",
                    key: "item_group",
                },
                {
                    title: "Catalog SKU",
                    key: "catalog_sku",
                },

                // {
                //     title: "In Stock Qty",
                //     key: "stock_value",
                // },
                {
                    title: "In Stock Qty",
                    key: "in_stock",
                },
                {
                    title: "Transfer In Qty",
                    key: "transferIn",
                },
                {
                    title: "Transfer Out Qty",
                    key: "transferOut",
                },
                {
                    title: "On Order Qty",
                    key: "onOrder",
                },
                {
                    title: "Unit Type",
                    key: "weight_uom",
                },
                {
                    title: "In Stock Cost",
                    key: "in_stockcost",
                },
                {
                    title: "Avg Unit Cost In Stock",
                    key: "avg_unit_stockcost",
                },
                {
                    title: "Regular Price",
                    key: "selling_price",
                },
                {
                    title: "Retail Value In Stock",
                    key: "retail_stockvalue",
                },
                // {
                //     title: "Profit Margin ($)",
                //     key: "profit_margin_amount",
                // },
                // {
                //     title: "Profit Margin (%)",
                //     key: "profit_margin_percent",
                // },
                // {
                //     title: "Markup",
                //     key: "markup",
                // },
                {
                    title: "First Received Date",
                    key: "formattedDateFirst",
                },
                {
                    title: "Last Received Date",
                    key: "formattedDateLast",
                },
                // {
                //     title: "Total Profit Margin ($)",
                //     key: "profit_margin_total",
                // },
                {
                    title: "Net Weight",
                    key: "net_weight",
                },
                {
                    title: "Total Net Weight In Stock",
                    key: "stock_net_weight",
                },
                {
                    title: "Days Since Last Sold",
                    key: "days_since_last_sold",
                },
                {
                    title: "Supplier",
                    key: "supplier_sku_name",
                },
                {
                    title: "Supplier SKU",
                    key: "supplier_sku",
                },
                {
                    title: "Product Status",
                    key: "product_status",
                },
            ],
        }
    },
    computed: {
        ...mapState("misc", ["warehouses", "itemGroups", "reasonCodes", "safeDrawerTransactionTypes", "REPORT_MASTER_CODES"]),
    },
    mounted() {
        this.intervalId = setInterval(() => {
            this.initialize()
        }, 1000)
    },
    beforeDestroy() {
        if (this.intervalId) {
            clearInterval(this.intervalId)
            this.intervalId = null
        }
    },
    methods: {
        initialize() {
            if (this.warehouses.length) {
                this.filters.push(
                    new ReportFilter(
                        "Report View",
                        [
                            { id: "product_location", title: "By Product & Location" },
                            { id: "product", title: "By Product" },
                            { id: "package", title: "By Package" },
                        ],
                        false,
                        "report_dimension"
                    ),
                    new ReportFilter(
                        "Locations",
                        [
                            ...this.warehouses.map((item) => ({
                                id: item.id,
                                title: item.name,
                            })),
                        ],
                        true,
                        "warehouse_ids"
                    ),
                    new ReportFilter(
                        "Classifications",
                        [
                            ...this.itemGroups.map((item) => ({
                                id: item.id,
                                title: item.name,
                            })),
                        ],
                        true,
                        "item_group_ids"
                    ),
                    new ReportFilter(
                        "In/Out Of Stock",
                        [
                            { id: "0", title: "In Stock" },
                            { id: "1", title: "Out of Stock" },
                            { id: "all", title: "All" },
                        ],
                        false,
                        "show_out_of_stock"
                    ),
                    new ReportFilter(
                        "Sort by",
                        [
                            { id: "", title: "Product Name A-Z (Default)" },
                            { id: "item_name", title: "Product Name A-Z" },
                            { id: "item_name_desc", title: "Product Name Z-A" },
                            { id: "brand", title: "Brand A-Z" },
                            { id: "brand_desc", title: "Brand Z-A" },
                            { id: "createdAt", title: "Date Added (Oldest)" },
                            { id: "createdAt_desc", title: "Date Added (Newest)" },
                        ],
                        false,
                        "sort_by"
                    )
                )
                clearInterval(this.intervalId)
                this.initialized = true
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../../assets/variables.scss";

.inventory-report-note {
    margin: 0 0 16px;
    padding: 12px 14px;
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    background: $lightCardBg;
    color: $textColor;
    font-size: 14px;
    line-height: 1.5;

    .dark & {
        border-color: $darkBorderColor;
        background: $darkCardBg;
        color: $darkTextColor;
    }
}
</style>
